import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Icon from '@components/Icon';
import { Upload, Button, Input, Divider } from 'antd';
import { supportImg, cn } from '@utils/utils';
import stores from '@stores';
import usePaste from '@hooks/usePaste';
import useSetImg from '@hooks/useSetImg';
import demoPng from '@assets/demo.png';

const { Dragger } = Upload;

export default observer(() => {
    const { t } = useTranslation('editor');
    const getFile = useSetImg(stores);
    const beforeUpload = async (file) => {
        await getFile(file);
        return Promise.reject();
    }
    
    const handleLoadDemo = () => {
        getFile(demoPng, 'dataURL');
    }
    
    const handleUrlSubmit = async (e) => {
        if (e.key === 'Enter') {
            const url = e.target.value.trim();
            if (url) {
                try {
                    await getFile(url, 'url');
                    e.target.value = '';
                } catch (error) {
                    stores.editor.message.error(t('init.uploadError'));
                }
            }
        }
    }
    
    usePaste((file) => {
        getFile(file);
    });
    
    return (
        <div className='flex-1 flex flex-col justify-center items-center overflow-hidden select-none relative bg-white/50 dark:bg-gray-800/30 backdrop-blur-sm mx-4 my-4 rounded-lg'>
            <div className={cn('max-w-[600px] w-full', stores.editor.invalid && 'invalid')}>
                <Dragger
                    accept={supportImg.join(',')}
                    name="file"
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    rootClassName="p-4 rounded-xl bg-white dark:bg-gray-800 dark:text-gray-300 block shadow-sm border-2 border-dashed border-purple-200 dark:border-purple-700 hover:border-purple-400 dark:hover:border-purple-500 transition-colors"
                >
                    <div className="text-center p-8">
                        <p className="text-2xl my-2 opacity-60">
                            <Icon.ImagePlus className="mx-auto text-purple-600 dark:text-purple-400" size={48} useFa={true} />
                        </p>
                        <p className="text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('init.clickOrDrag')}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('init.formatSupport')}
                        </p>
                    </div>
                </Dragger>

                <div className="mt-4 flex flex-col gap-3">
                    <Input
                        placeholder={t('init.urlPlaceholder')}
                        prefix={<Icon.Link size={16} className="text-gray-400" />}
                        className="rounded-lg py-2"
                        onKeyPress={handleUrlSubmit}
                    />
                    
                    <div className="relative">
                        <Divider className="my-4">
                            <span className="text-xs text-gray-500 dark:text-gray-400 px-4">{t('init.or')}</span>
                        </Divider>
                    </div>

                    <Button 
                        type="default"
                        onClick={handleLoadDemo}
                        className="w-full py-6 flex items-center justify-center gap-4 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-100 dark:border-purple-800 hover:shadow-md hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-300"
                    >
                        <div className="overflow-hidden rounded-lg w-12 h-12 flex-shrink-0 shadow-sm">
                            <img src={demoPng} className="w-full h-full object-cover" alt={t('init.demoImageAlt')} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-base font-medium text-gray-700 dark:text-gray-200">
                                {t('init.loadDemoImage')}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {t('init.quickExperience')}
                            </span>
                        </div>
                    </Button>
                </div>
            </div>
        </div>
    )
});